import { useTranslation } from 'react-i18next';
import type { TechnicianLicenseSummary } from '@/routes/technicians/useTechnicianLicenses';

/**
 * "2 expired" / "1 expiring soon" (M1-37) — the badge the AC asks for on both
 * the technician's profile and the roster, so it is one component used twice
 * rather than two that can drift apart.
 *
 * <b>It adds no colour tokens</b>, the same decision the dispatch board made
 * (web/CONVENTIONS.md): expired reuses `--color-destructive`, which the
 * checked-palette table already clears against `--color-background`, and
 * expiring-soon is a plain `--color-muted` chip. A red/amber/green rainbow
 * would mean three new tokens and six new pairings to verify, and it would
 * STILL need the words — so the words do the work and the colour only
 * emphasises them.
 *
 * Renders nothing when everything is current. An "all good" badge on every row
 * of a roster is noise that trains people to stop reading the column, which is
 * the one thing this badge cannot afford.
 */
export function LicenseExpiryBadge({
  summary,
  className = '',
}: {
  summary: TechnicianLicenseSummary | undefined;
  className?: string;
}) {
  const { t } = useTranslation();

  // Number(), because the OpenAPI generator widens int32 to `number | string`
  // — the same quirk web/CONVENTIONS.md already documents for `sortOrder` and
  // `latitude`/`longitude`. A bare `> 0` on the union does not compile, and a
  // `=== 0` would silently be false for the string "0".
  const expired = Number(summary?.expiredCount ?? 0);
  const expiringSoon = Number(summary?.expiringSoonCount ?? 0);

  if (!summary || (expired === 0 && expiringSoon === 0)) {
    return null;
  }

  // Both can be non-zero at once — a technician with a lapsed backflow ticket
  // and a journeyman card due next month — so these are two chips, not a
  // priority pick between them. Expired first: it is the actionable one.
  return (
    <span data-testid="license-expiry-badge" className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {expired > 0 ? (
        <span className="inline-block rounded-full border border-[var(--color-destructive)] px-2 py-0.5 text-xs font-medium text-[var(--color-destructive)]">
          {t('technicianLicenses.badge.expired', { count: expired })}
        </span>
      ) : null}
      {expiringSoon > 0 ? (
        <span className="inline-block rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs font-medium">
          {t('technicianLicenses.badge.expiringSoon', { count: expiringSoon })}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The same signal for ONE licence rather than a whole technician — the chip
 * shown against a row in the licences list.
 *
 * `Valid` and `NoExpiry` deliberately render nothing here too, for the same
 * reason: in a list of five licences, the two that need attention should be
 * the only two carrying a chip.
 */
export function LicenseRowExpiryChip({
  expiryState,
  daysUntilExpiry,
}: {
  expiryState: string;
  daysUntilExpiry: number | null;
}) {
  const { t } = useTranslation();

  if (expiryState === 'Expired') {
    return (
      <span className="inline-block rounded-full border border-[var(--color-destructive)] px-2 py-0.5 text-xs font-medium text-[var(--color-destructive)]">
        {t('technicianLicenses.row.expired', { count: Math.abs(daysUntilExpiry ?? 0) })}
      </span>
    );
  }

  if (expiryState === 'ExpiringSoon') {
    return (
      <span className="inline-block rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs font-medium">
        {t('technicianLicenses.row.expiringSoon', { count: daysUntilExpiry ?? 0 })}
      </span>
    );
  }

  return null;
}
